import React from 'react';

// Estado de error compartido de VLS: "no se pudo cargar" sobrio, sin rojo de
// alarma (la pantalla no está en peligro, solo falló una carga), con botón
// Reintentar. Para errores de validación de un formulario sigue siendo el
// Alert/texto en rojo junto al campo; esto es para cargas que fallan.
//
// ─── Migración (mecánica) ────────────────────────────────────────────────
//   ANTES:
//     {error && <Alert variant="error">{error}</Alert>}
//   DESPUÉS:
//     {error && <ErrorState message={error} onRetry={load} />}
//
// Props: title ('No se pudo cargar' por defecto), message (o children),
// onRetry (sin él no hay botón), retryLabel ('Reintentar'), retrying (el
// botón queda ocupado con "Reintentando…"), size 'md' | 'sm', framed
// (tarjeta con borde, por defecto true), className (solo layout).
// Sin dependencias; colores por var(--color-*) con respaldo.

const C = {
  card: 'var(--color-card, #FFFFFF)',
  fg: 'var(--color-foreground, #1C1917)',
  muted: 'var(--color-muted, #F5F5F4)',
  mutedFg: 'var(--color-muted-foreground, #78716C)',
  border: 'var(--color-border, #E7E5E4)',
};

const Nube = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
    <line x1="12" y1="11" x2="12" y2="14" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
const Girar = ({ girando }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    className={girando ? 'animate-spin' : undefined}>
    <path d="M21 12a9 9 0 1 1-2.64-6.36" /><polyline points="21 3 21 9 15 9" />
  </svg>
);

export default function ErrorState({
  title = 'No se pudo cargar', message, children, onRetry, retryLabel = 'Reintentar', retrying = false,
  size = 'md', framed = true, className = '', 'data-testid': testId,
}) {
  const sm = size === 'sm';
  const texto = message ?? children;
  return (
    <div
      role="alert"
      data-vls-error=""
      data-testid={testId}
      className={`flex flex-col items-center text-center ${framed ? 'border rounded-xl' : ''} ${className}`}
      style={{
        gap: sm ? 6 : 10,
        padding: sm ? '16px 12px' : '32px 16px',
        ...(framed ? { background: C.card, borderColor: C.border } : null),
      }}
    >
      <span
        aria-hidden="true"
        className="inline-flex items-center justify-center rounded-full"
        style={{ width: sm ? 36 : 48, height: sm ? 36 : 48, background: C.muted, color: C.mutedFg }}
      >
        <Nube />
      </span>
      <p className={sm ? 'text-sm font-medium' : 'text-base font-semibold'} style={{ margin: 0, color: C.fg }}>{title}</p>
      {texto && <div className="text-sm" style={{ color: C.mutedFg, maxWidth: '48ch' }}>{texto}</div>}
      {onRetry && (
        <button
          type="button"
          onClick={() => onRetry()}
          disabled={retrying}
          aria-busy={retrying || undefined}
          className="inline-flex items-center justify-center text-sm font-medium rounded-lg border hover:bg-black/5 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          style={{ minHeight: 44, padding: '0 16px', gap: 8, marginTop: sm ? 4 : 8, borderColor: C.border, background: C.card, color: C.fg }}
        >
          <Girar girando={retrying} />
          {retrying ? 'Reintentando…' : retryLabel}
        </button>
      )}
    </div>
  );
}
